import { useRef, useState, type FormEvent } from 'react'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { Button, Input } from '@/components/ui'
import { useAuth } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'

interface LoginFormProps {
  onSuccess?: () => void
}

const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY ?? ''

export function LoginForm({ onSuccess }: LoginFormProps) {
  const { signIn, isConfigured } = useAuth()
  const { showToast } = useToast()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSendingLink, setIsSendingLink] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const captchaRef = useRef<HCaptcha>(null)

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!email.trim()) {
      newErrors.email = 'Email is required'
    }

    if (!password) {
      newErrors.password = 'Password is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validate()) return

    if (!isConfigured) {
      showToast('Supabase is not configured. Check your environment variables.', 'error')
      return
    }

    if (!captchaToken) {
      setErrors({ captcha: 'Please complete the captcha to continue' })
      return
    }

    setIsLoading(true)
    setErrors({})

    const { error } = await signIn(email, password, captchaToken)

    // hCaptcha tokens are single-use — reset so a retry gets a fresh one.
    captchaRef.current?.resetCaptcha()
    setCaptchaToken(null)

    setIsLoading(false)

    if (error) {
      if (error.message.includes('Invalid login')) {
        setErrors({ password: 'Invalid email or password' })
      } else if (error.message.includes('Email not confirmed')) {
        showToast('Please check your email and click the verification link.', 'info')
      } else {
        showToast(error.message, 'error')
      }
      return
    }

    showToast('Welcome back!', 'success')
    onSuccess?.()
  }

  const handleSendSigninLink = async () => {
    if (!email.trim()) {
      setErrors({ email: 'Enter your email to get a sign-in link' })
      return
    }
    setErrors({})
    setIsSendingLink(true)
    try {
      const res = await fetch('/api/send-signin-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      })
      const body = await res.json().catch(() => ({}))
      if (res.status === 429) {
        showToast(body.error || 'Too many requests. Please try again later.', 'error')
      } else {
        showToast(
          body.message ||
            "If that email is registered, we've sent a sign-in link. Check your inbox.",
          'success',
        )
      }
    } catch {
      showToast('Could not send sign-in link. Please try again.', 'error')
    } finally {
      setIsSendingLink(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Email"
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        required
        autoFocus
      />

      <Input
        label="Password"
        type="password"
        placeholder="Your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={errors.password}
        required
      />

      <div>
        <HCaptcha
          ref={captchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          onVerify={(token) => setCaptchaToken(token)}
          onExpire={() => setCaptchaToken(null)}
          onError={() => setCaptchaToken(null)}
        />
        {errors.captcha && (
          <p className="mt-1 text-sm text-[var(--color-kids-danger)]">{errors.captcha}</p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full"
        size="lg"
        isLoading={isLoading}
        disabled={!captchaToken}
      >
        Log In
      </Button>

      <div className="text-center pt-1">
        <button
          type="button"
          onClick={handleSendSigninLink}
          disabled={isSendingLink || isLoading}
          className="text-sm text-[var(--color-kids-primary)] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSendingLink ? 'Sending sign-in link…' : 'Forgot your password? Email me a sign-in link'}
        </button>
      </div>
    </form>
  )
}
